import React, { FC, useState, useRef } from 'react';
import Svg from 'svg-inline-react';
import classnames from 'classnames';
import GlobeSvg from '@shared/assets/svgs/globe.svg';
import LaptopSvg from '@shared/assets/svgs/laptop.svg';
import { OutsideClick } from '@src/components/OutsideClick';
import { Image } from '@src/components/Image';
import FrChevronDown from '@src/svgs/fr-expand.svg';
import FrSearchSvg from '@src/svgs/fr-search.svg';
import { TextField } from '@src/components/TextField';
import { RenderOnScroll } from '@src/components/RenderOnScroll';
import { getCountryFlagUrl } from '@src/util/countryFlag';
import { CountrySelectProps } from './props';
import './styles.scss';

export const REMOTE_OPTION = {
  id: 'remote',
  longName: 'Remote',
  shortName: 'Remote',
};

export const RELOCATION_OPTION = {
  id: 'relocation',
  longName: 'Relocation',
  shortName: 'Relocation',
};

export const GLOBAL_OPTION = {
  id: 'global',
  longName: 'Globally',
  shortName: 'Globally',
};

export const CountrySelect: FC<CountrySelectProps> = ({
  className,
  countries,
  defaultDisplayValue,
  selectedCountry,
  onSelectCountry,
  onSearch,
}) => {
  const scrollableRef = useRef<HTMLDivElement>(null);
  const [searchPattern, setSearchPattern] = useState('');

  const [isShown, setIsShown] = useState(false);

  const onChange = (searchPattern: string): void => {
    setSearchPattern(searchPattern);

    onSearch(searchPattern);
  };

  return (
    <OutsideClick addOutsideClickListener={isShown} onOutsideClick={() => setIsShown(false)}>
      <div className={classnames('country-select', className)}>
        <div className="country-select__selected" onClick={() => setIsShown(!isShown)}>
          <div className="country-select__selected-country">
            {selectedCountry?.id === 'remote' || selectedCountry?.id === 'relocation' ? (
              <Svg className="country-select__selected-dropdown-icon" src={GlobeSvg} />
            ) : (
              selectedCountry?.id && (
                <Image className="country-select__selected-flag" src={getCountryFlagUrl(selectedCountry?.shortName)} />
              )
            )}

            {selectedCountry?.longName || defaultDisplayValue}
          </div>

          <Svg
            className={classnames('country-select__button-icon', isShown && 'country-select__button-icon--rotate')}
            src={FrChevronDown}
          />
        </div>

        {isShown && (
          <div className="country-select__dropdown" ref={scrollableRef}>
            <div className="country-select__search-wrapper">
              <div className="country-select__search-inner">
                <Svg className="country-select__search-icon" src={FrSearchSvg} />

                <TextField
                  containerClassName="country-select__search-input-container"
                  defaultValue={searchPattern}
                  fullWidth
                  inputClassName="country-select__search-input"
                  onChange={value => onChange(value)}
                  placeholder="Search...."
                  wrapperClassName="country-select__search-input-wrapper"
                />
              </div>
            </div>

            <div
              className="country-select__dropdown-item"
              onClick={() => {
                onSelectCountry(REMOTE_OPTION);

                setIsShown(false);
              }}
            >
              <Svg className="country-select__dropdown-icon" src={LaptopSvg} />

              <div className="country-select__dropdown-label">Remote</div>
            </div>

            <div
              className="country-select__dropdown-item"
              onClick={() => {
                onSelectCountry(RELOCATION_OPTION);

                setIsShown(false);
              }}
            >
              <Svg className="country-select__dropdown-icon" src={GlobeSvg} />

              <div className="country-select__dropdown-label">Relocation</div>
            </div>

            <RenderOnScroll renderLimit={20} scrollRef={scrollableRef}>
              {countries.map(country => {
                return (
                  <div
                    className="country-select__dropdown-item"
                    key={country.id}
                    onClick={() => {
                      onSelectCountry(country);

                      setIsShown(false);
                    }}
                  >
                    <Image className="country-select__flag" src={getCountryFlagUrl(country.shortName)} />

                    <div className="country-select__dropdown-label">{country.longName}</div>
                  </div>
                );
              })}
            </RenderOnScroll>
          </div>
        )}
      </div>
    </OutsideClick>
  );
};
